import styled from '@emotion/styled';
import { useCommonModal } from 'hooks/useCommonModal';
import { createContext, useRef, useState, useEffect } from 'react';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { GButtonBox, GPopupTitle } from 'components/layouts/GLayoutBox';
import { DialogTitleItem } from 'components/layouts/GModalPopup';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { GButton } from 'components/atom/button';
import Save from 'components/asset/images/Confirm.svg';
import {
  GButtonBoxCum,
  SearchItemWrap,
  SearchWrap,
  UnHiddenSearchWrap,
} from 'components/molecule/SearchMolecule';
import GButtonLayout from 'components/molecule/GbuttonLayout';
import Search from 'components/asset/images/Search.svg';
import { GContentGrid } from 'components/layouts/GContentGrid';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'components/process/MessageBar';
import ModalLayout from 'components/layouts/ModalLayout';
import CSearchMolecule from 'components/vars/common/CSearchMolecule';
import {
  getAdminManagerListApi,
  getAdminManagerListApiByCopCd,
  getUserManagerListApi,
  getUserManagerListApiByCopCd,
} from 'apis/common/ManagerSearchApi';
import { Employee } from 'models/admin/Employee';
import useAuthStore from 'stores/useAuthStore';
import { MemberRegVO } from 'pages/vars/member/signup/MemberRegistPage';
import { useManagerSearchSingleColumnDefs } from './hooks/ManagerSearchSingleColumnDefs';
import { MemberEditVO } from 'pages/vars/member/manage/MemberDetailPage';
import EmptyState from 'components/atom/emptyState';
import { CommonCode } from 'models/admin/CommonCode';
import { CommonResponse } from 'models/common/RestApi';
import _ from 'lodash';

type SearchModalProps = {
  id: string;
  open: boolean;
  singleSelect?: boolean;
  close: () => void;
  save?: (param) => void;
  // 추가
  signUpFormData: MemberRegVO | MemberEditVO;
  setSignUpFormData: any;
  selectedManagerData: Employee[];
  setSelectedManagerData: React.Dispatch<React.SetStateAction<Employee[]>>;
  coCdList?: CommonCode[];
};

// 현재 사용자 검색 팝업 중 사용은 ManagerSearchModalSingle 하고 있음
const ManagerSearchModalSingle = (props: SearchModalProps) => {
  const { t } = useTranslation();
  const t2 = t as (msgId: string, defVal: string) => string;
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const gridRef = useRef<AgGridReact<Employee>>(null);
  const { accessToken } = useAuthStore();

  const [managerData, setManagerData] = useState<Employee[]>([]);
  const { defaultColDef, colDefs } = useManagerSearchSingleColumnDefs();
  const [inputValue, setInputValue] = useState<string>('');

  const handleError = (e: Error) => {
    openLoading(false);
    // e.message 대신 Failed to Search 로 변경
    openMessageBar('error', 'Failed to Search');
  };

  function isErrorMessage(data: unknown): data is string {
    return typeof data === 'string';
  }

  function searchProcessing(res: CommonResponse<Employee[]>, inputValue: string) {
    if (res.successOrNot === 'Y') {
      const result = res.data as Employee[];
      setManagerData(result);
    } else {
      if (isErrorMessage(res.data)) {
        //
        const errorMessage = res.data as string;

        if ('A' === errorMessage) {
          openCommonModal({
            modalType: 'alert',
            content: t('com.label.emptyEmp', '__입력하신 메일에 해당하는 임직원이 없습니다.'),
          });
        }
        if ('B' === errorMessage) {
          //
          if (props.coCdList) {
            const soprMgrCopNm = props.coCdList.filter(
              (item) => props.signUpFormData.soprMgrCopCd === item.cmnCd
            );
            openCommonModal({
              modalType: 'alert',
              content: t('com.label.validMgr', {
                copCdNm: soprMgrCopNm[0].cmnCdNm,
                soprMgrEmal: inputValue,
              }),
            });
          }
        }
      }
      setManagerData([]);
    }
  }

  const getManagerList = async () => {
    // 회원가입 시 일반인user API 탐
    // VARS DB에 인사 정보에 없는 것과 TRMS 유효한 권한자 조회시 없는 것 구분해서 메세지 출력 해야함
    // I/F 개발 전 까지 이전 소스로 getUserManagerListApiByCopCd(inputValue, copCd)/getAdminManagerListApiByCopCd(inputValue, copCd) 대신
    // getUserManagerListApi(inputValue) / getAdminManagerListApi(inputValue) 사용하고 searchProcessing은 주석
    const copCd = props.signUpFormData.soprMgrCopCd ?? '';
    if (props.id === 'signup' || accessToken) {
      // I/F 테스트 중
      getUserManagerListApiByCopCd(inputValue, copCd)
        .then((res) => {
          searchProcessing(res, inputValue);
          // setManagerData([...res]);
        })
        .catch(handleError);
    } else {
      console.log('getAdminManagerListApi  호출하는 경우');
      getAdminManagerListApiByCopCd(inputValue, copCd)
        .then((res) => {
          searchProcessing(res, inputValue);
          // setManagerData([...res]);
        })
        .catch(handleError);
    }
  };

  const handleChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSearch = () => {
    // 조회값 입력하지 않은 경우
    if (_.isEmpty(inputValue)) {
      // com.label.00496
      openCommonModal({
        modalType: 'alert',
        content: t('com.label.00496', '__담당자 이메일을 입력하세요.'),
      });
      openLoading(false);
      return;
    }
    // 메일 양식이 아닌 경우
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    console.log('emailRegex.test(inputValue)', emailRegex.test(inputValue));
    // com.label.00496
    if (!emailRegex.test(inputValue)) {
      // openMessageBar('warning', t('com.label.00230', '__이메일 형식이 올바르지 않습니다.'));
      openCommonModal({
        modalType: 'alert',
        content: t('com.label.00230', '__이메일 형식이 올바르지 않습니다.'),
      });
      openLoading(false);
      return;
    }
    getManagerList();
  };

  const handleClose = () => {
    // 조회 입력값만 초기화 하고, 조회된 데이타는 초기화는 주석 처리 된 것으로 보아 일부러 초기화 안 하는듯
    setInputValue('');
    // 초기화 필요해서 주석 품
    setManagerData([]);
    // props.setSelectedManagerData([]);
    props.close();
  };

  const handleOnSave = () => {
    const selectedRows = gridRef.current?.api.getSelectedRows() || [];
    console.log('선택된 행 값 ::', selectedRows?.[0]);
    if (selectedRows.length === 0) {
      openCommonModal({
        modalType: 'alert',
        content: t('com.msg.noSelRow', '__선택된 행이 없습니다.'),
      });
      return;
    } else {
      props.setSelectedManagerData(selectedRows);
      props.setSignUpFormData({
        ...props.signUpFormData,
        soprMgrNm: selectedRows?.[0]?.empNm ?? '',
        soprMgrEmal: selectedRows?.[0]?.emlSvrDmnIfoNm ?? '',
        soprMgrEmpNo: selectedRows?.[0]?.empNo ?? '',
        userId: selectedRows?.[0]?.userId ?? '',
      });
      // close 하고 조회 입력값 출력된 데이타 초기화
      // props.close();
      handleClose();
    }
  };

  return (
    <ModalLayout open={props.open} onClose={handleClose} mMinWidth={1000} mMaxWidth={1000}>
      <DialogTitleItem onClose={handleClose}>
        <GPopupTitle title2={t('com.label.mngSrch', '__담당자 검색')} />
      </DialogTitleItem>
      <DialogContent className="popupContent">
        <SearchWrap>
          <UnHiddenSearchWrap>
            <ItemWrap>
              <CSearchMolecule
                type="input"
                labelTitle={t('com.label.mngEml', '__담당자 이메일')}
                placeholder={t2('com.label.00496', '__담당자 이메일을 입력하세요')}
                searchWidth="100%"
                searchValueWidth="350px"
                onChange={handleChange}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
              />
            </ItemWrap>
            <GButtonLayout paddingTop="0px" marginBottom="0px">
              <GButtonBoxCum>
                <GButton
                  chkImg={Search}
                  txt={t('com.label.00116', '__조회')}
                  sizes="medium"
                  btnstyled="contained"
                  btnColor="normal"
                  hasImg={true}
                  hasTxt={true}
                  onClick={handleSearch}
                ></GButton>
              </GButtonBoxCum>
            </GButtonLayout>
          </UnHiddenSearchWrap>
        </SearchWrap>
        <GContentGrid
          type="radio"
          className="ag-theme-alpine"
          isSortable={false}
          style={{ height: '336px' }}
        >
          <AgGridReact
            ref={gridRef}
            defaultColDef={defaultColDef}
            columnDefs={colDefs}
            rowData={managerData}
            rowSelection={'single'}
            suppressMovableColumns
            enableCellTextSelection
            tooltipShowDelay={0}
            tooltipHideDelay={2000}
            noRowsOverlayComponent={EmptyState}
            noRowsOverlayComponentParams={{ type: 'grid' }}
          />
        </GContentGrid>
      </DialogContent>
      <DialogActions className="popupBottom">
        <GButtonBox>
          <GButton
            txt={t('com.label.00036', '취소')}
            sizes="medium"
            onClick={handleClose}
            btnstyled="outline"
            btnColor="normal"
            hasImg={false}
            hasTxt={true}
          />
          <GButton
            txt={t('com.label.00023', '선택')}
            sizes="medium"
            onClick={handleOnSave}
            chkImg={Save}
            btnstyled="contained"
            btnColor="primary"
            hasImg={true}
            hasTxt={true}
          />
        </GButtonBox>
      </DialogActions>
    </ModalLayout>
  );
};
export default ManagerSearchModalSingle;

const ItemWrap = styled(SearchItemWrap)`
  display: flex;
  gap: 24px;
`;
